(function attachJudesBundleBuilder(root) {
  'use strict';

  document.documentElement.classList.add('judes-bundle-page');

  function checkoutProgressText(language) {
    return {
      de: 'Checkout wird vorbereitet …',
      en: 'Preparing checkout …',
      fr: 'Préparation du paiement …',
      es: 'Preparando el pago …',
      it: 'Preparazione del checkout …'
    }[String(language || 'en').slice(0, 2)] || 'Preparing checkout …';
  }

  function setCheckoutProgress(button, active, restingText, language, busyText) {
    if (active) {
      button.textContent = busyText || checkoutProgressText(language);
      button.setAttribute('aria-busy', 'true');
      return;
    }
    button.textContent = restingText;
    button.removeAttribute('aria-busy');
  }

  function whenReady(callback) {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', callback, { once: true });
    } else {
      callback();
    }
  }

  function parseJsonScript(element, label) {
    if (!element) throw new Error(`${label} is missing`);
    try {
      return JSON.parse(element.textContent);
    } catch (error) {
      throw new Error(`${label} contains invalid JSON`);
    }
  }

  function moneyFormatter(currency) {
    return new Intl.NumberFormat(document.documentElement.lang || 'en', {
      style: 'currency',
      currency,
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    });
  }

  function escapeHtml(value) {
    return String(value)
      .replaceAll('&', '&amp;')
      .replaceAll('<', '&lt;')
      .replaceAll('>', '&gt;')
      .replaceAll('"', '&quot;')
      .replaceAll("'", '&#039;');
  }

  function displayTitle(value) {
    const title = String(value || '').split(' / ')[0];
    return title === 'Default Title' ? '' : title;
  }

  function scrollBehavior() {
    return 'auto';
  }

  function interpolate(template, values) {
    return Object.entries(values).reduce((result, [key, value]) => (
      result.replaceAll(`{${key}}`, String(value))
    ), String(template || ''));
  }

  function sizedImageUrl(value, size) {
    const url = String(value || '');
    const width = Math.min(480, Math.max(96, Math.ceil((size || 96) * 2)));
    if (/([?&])width=\d+/.test(url)) return url.replace(/([?&])width=\d+/, `$1width=${width}`);
    return `${url}${url.includes('?') ? '&' : '?'}width=${width}`;
  }

  function newBundleId(recipeId) {
    if (root.crypto && typeof root.crypto.randomUUID === 'function') {
      return `${recipeId}-${root.crypto.randomUUID()}`;
    }
    return `${recipeId}-${Date.now()}-${Math.random().toString(16).slice(2)}`;
  }

  function createBuilder(element) {
    const core = root.JudesBundleCore;
    const checkout = root.JudesSetCheckout;
    const commerce = root.JudesSetCommerce;
    const startPrice = root.JudesSetStartPrice;
    const recipeSource = root.JudesBundleRecipes;
    const copy = parseJsonScript(element.querySelector('[data-bundle-copy]'), 'Bundle copy');
    const catalog = parseJsonScript(element.querySelector('[data-bundle-catalog]'), 'Bundle catalog');
    const startPriceNode = element.querySelector('[data-set-start-price-manifest]');
    const serverSeedNode = element.querySelector('[data-set-server-seed]');
    let startPriceManifest = startPrice && startPriceNode
      ? startPrice.parse(startPriceNode.textContent) : null;
    const productMedia = {
      klassik: parseJsonScript(element.querySelector('[data-bundle-classic-media]'), 'Classic product media'),
      premium: parseJsonScript(element.querySelector('[data-bundle-premium-media]'), 'Premium product media')
    };
    const formatter = moneyFormatter(element.dataset.currency || 'EUR');
    const label = (value) => copy.labels[value] || value;
    const translatedPerks = (currentRecipe) => copy.perks[currentRecipe.routineKey] || currentRecipe.perks;
    const routesRoot = element.dataset.routesRoot.endsWith('/') ? element.dataset.routesRoot : `${element.dataset.routesRoot}/`;
    const bundleCoachingImage = element.dataset.bundleCoachingImage || '';

    if (!core) throw new Error('Bundle core is unavailable');
    if (!checkout) throw new Error('Bundle checkout is unavailable');
    if (!recipeSource || !Array.isArray(recipeSource.recipes)) throw new Error('Bundle recipes are unavailable');
    if (!productMedia.klassik.length || !productMedia.premium.length) throw new Error('Product media is unavailable');

    const startklar = root.JudesStartklar;
    const entry = startklar ? startklar.entry(root.location.href, recipeSource.recipes) : { enabled: false, recipe: null };
    // Version guard: only the offer-aware judes-startklar.js (it has render) gets the entry object; an older one keeps the live boolean.
    const startklarState = startklar && typeof startklar.render === 'function' ? entry : entry.enabled;
    // Capture once, before Klarna or recipe synchronization rewrites history.
    // An explicit entry variant stays exact-only for the whole document.
    const cacheRouteEligible = !!(startPrice
      && startPrice.routeAllowsCache(root.location.href, entry.enabled));
    let recipe = entry.recipe || recipeSource.recipes.find((candidate) => candidate.id === element.dataset.initialRecipeId)
      || recipeSource.recipes.find((candidate) => candidate.parentVariantId === element.dataset.parentVariantId)
      || recipeSource.recipes.find((candidate) => candidate.id === element.dataset.previewRecipeId);
    if (!recipe) throw new Error('No recipe is configured for this set');

    // Design availability (2026-09-30). A design is selectable when the recipe allows it and Shopify reports it
    // available in the buyer's market (the catalog `available` flag, rendered in the market context). Quantity
    // limits are learned only from this market's exact Shopify quote (stock shortfall), never from all-location
    // totals, so US stock never makes a design available in Germany. Defaults, presets, size switches and picks
    // never keep an unavailable design. Most Popular uses rank order; Soft Pastels prefers its current group.
    // Manual picks and other presets keep same-collection substitution, then the ranking.
    const marketLimits = new Map();

    function limitOf(variantId) {
      return marketLimits.has(String(variantId)) ? marketLimits.get(String(variantId)) : Infinity;
    }

    function designAvailable(variantId) {
      return Boolean(catalog[variantId] && catalog[variantId].available) && limitOf(variantId) > 0;
    }

    function designSelectable(group, variantId) {
      return group.allowedVariantIds.includes(variantId) && designAvailable(variantId);
    }

    function designCollections(group) {
      return Array.isArray(group.designGroups) ? group.designGroups.filter((designGroup) => !designGroup.limit) : [];
    }

    function designRanking(group) {
      return Array.isArray(group.designGroups)
        ? group.designGroups.filter((designGroup) => designGroup.limit).flatMap((designGroup) => [...designGroup.variantIds])
        : [];
    }

    // The same design in another choice group (the other size), matched by design name.
    function counterpartIn(group, variantId) {
      if (group.allowedVariantIds.includes(variantId)) return variantId;
      const design = displayTitle(catalog[variantId]?.title);
      return design ? group.allowedVariantIds.find((candidateId) => displayTitle(catalog[candidateId]?.title) === design) || null : null;
    }

    function substitutionOrder(group, variantId, presetId) {
      const collection = (presetId === 'most-popular' || presetId === 'soft-pastels')
        ? (group.designGroups || []).find((designGroup) => designGroup.id === presetId)
        : designCollections(group).find((designGroup) => designGroup.variantIds.includes(variantId));
      const ordered = [];
      if (collection) {
        const ids = [...collection.variantIds];
        const position = ids.indexOf(variantId);
        ordered.push(...ids.slice(position + 1), ...ids.slice(0, position));
      }
      ordered.push(...designRanking(group), ...group.allowedVariantIds);
      return [...new Set(ordered)].filter((candidateId) => candidateId !== variantId);
    }

    // entries: one { variantId, source } per slot; source is the choice group the design came from.
    function resolveSlots(group, entries, presetId) {
      const counts = new Map();
      const fits = (variantId) => designSelectable(group, variantId) && (counts.get(variantId) || 0) < limitOf(variantId);
      const take = (variantId) => {
        counts.set(variantId, (counts.get(variantId) || 0) + 1);
        return variantId;
      };
      return entries.map(({ variantId, source, fallbackPresetId }, index) => {
        const own = counterpartIn(group, variantId);
        if (own && fits(own)) return take(own);
        // Next matching design: the source collection order mapped into this group, then this slot's default
        // and its own collection order. Beyond known quantity only when nothing else is available.
        const fallback = group.defaultVariantIds[index];
        const candidates = substitutionOrder(source || group, variantId, fallbackPresetId || presetId)
          .map((candidateId) => counterpartIn(group, candidateId)).filter(Boolean)
          .concat([fallback], substitutionOrder(group, fallback, fallbackPresetId || presetId));
        const next = candidates.find(fits) || candidates.find((candidateId) => designSelectable(group, candidateId));
        return take(next || own || fallback);
      });
    }

    function resolveSelections(targetRecipe, selections, presetId) {
      return Object.fromEntries(targetRecipe.choiceGroups.map((group) => {
        const chosen = selections && Array.isArray(selections[group.id]) ? selections[group.id] : group.defaultVariantIds;
        const entries = Array.from({ length: group.quantity }, (_, index) => ({
          variantId: String(chosen[index] ?? group.defaultVariantIds[index]),
          source: group
        }));
        return [group.id, resolveSlots(group, entries, presetId)];
      }));
    }

    // Resolve shortcuts against this market before cycling their palette. Most Popular takes the first six
    // available ranked designs; Soft Pastels stays in the current collection while any are available.
    function resolvePreset(targetRecipe, preset) {
      if (!preset || !['most-popular', 'soft-pastels'].includes(preset.id)) {
        return resolveSelections(targetRecipe, preset && preset.selections);
      }
      const selections = Object.fromEntries(targetRecipe.choiceGroups.map((group) => {
        const collection = (group.designGroups || []).find((candidate) => candidate.id === preset.id);
        let palette = collection
          ? collection.variantIds.filter((variantId) => designSelectable(group, variantId))
          : [...new Set(preset.selections[group.id])].filter((variantId) => designSelectable(group, variantId));
        if (collection && collection.limit) palette = palette.slice(0, collection.limit);
        if (!palette.length) palette = preset.selections[group.id];
        return [group.id, Array.from({ length: group.quantity }, (_, index) => palette[index % palette.length])];
      }));
      return resolveSelections(targetRecipe, selections, preset.id);
    }

    function sameSelections(left, right) {
      return JSON.stringify(left) === JSON.stringify(right);
    }

    const state = {
      selections: resolvePreset(recipe, recipe.presets && recipe.presets[0]),
      bundleId: newBundleId(recipe.id),
      presetId: recipe.presets && recipe.presets[0] ? recipe.presets[0].id : '',
      mediaType: recipe.bundleType,
      mediaIndex: 0
    };

    const query = (selector) => element.querySelector(selector);
    const price = query('[data-bundle-price]');
    const retailValue = query('[data-bundle-retail-value]');
    const productHeading = query('[data-bundle-product-heading]');
    const giftValue = query('[data-bundle-gift-value]');
    const giftSection = query('[data-bundle-gift-section]');
    const giftHeading = query('[data-bundle-gift-heading]');
    const giftStrip = query('[data-bundle-gift-strip]');
    const routine = query('[data-bundle-routine]');
    const summaryRoutine = query('[data-bundle-summary-routine]');
    const summarySize = query('[data-bundle-summary-size]');
    const description = query('[data-bundle-description]');
    const perks = query('[data-bundle-perks]');
    const included = query('[data-bundle-included]');
    const groups = query('[data-bundle-groups]');
    const impactShare = query('[data-bundle-impact-share]');
    const impactShareLabel = query('[data-bundle-impact-share-label]');
    const impactPlastic = query('[data-bundle-impact-plastic]');
    const impactSavings = query('[data-bundle-impact-savings]');
    const summaryBenefits = query('[data-bundle-summary-benefits]');
    const presets = query('[data-bundle-presets]');
    const summary = query('[data-bundle-summary]');
    const errorBox = query('[data-bundle-error]');
    const klarnaWrapper = query('[data-bundle-klarna]');
    const configPanel = query('[data-bundle-config]');
    const summaryPanel = query('[data-bundle-summary-panel]');
    const buildButton = query('[data-bundle-build]');
    const stickyEntry = query('[data-bundle-sticky-entry]');
    const stickyBuildButton = query('[data-bundle-sticky-build]');
    const reviewButton = query('[data-bundle-review]');
    const backButton = query('[data-bundle-back]');
    const checkoutButton = query('[data-bundle-checkout]');
    const checkoutButtonText = checkoutButton.textContent;
    const checkoutLanguage = document.documentElement.lang || 'en';
    const checkoutBusyText = copy.checkoutBusy || checkoutProgressText(checkoutLanguage);
    const sheet = query('[data-bundle-sheet]');
    const sheetTitle = query('[data-bundle-sheet-title]');
    const sheetBody = query('[data-bundle-sheet-body]');
    const sheetLink = query('[data-bundle-sheet-link]');
    const sheetClose = query('[data-bundle-sheet-close]');
    const heroMedia = query('.judes-bundle__media');
    const heroImage = query('[data-bundle-hero-image]');
    const heroPrevious = query('[data-bundle-hero-prev]');
    const heroNext = query('[data-bundle-hero-next]');
    const photoSwipeModuleUrl = element.dataset.bundlePhotoswipeModule;
    const galleryArrowLeft = element.dataset.bundleGalleryArrowLeft;
    const galleryArrowRight = element.dataset.bundleGalleryArrowRight;
    let sheetTrigger = null;
    let suppressHeroClick = false;
    let activePhotoSwipe = null;
    let photoSwipeModulePromise = null;
    const overlayLocks = new Set();
    const stickyMobileQuery = root.matchMedia ? root.matchMedia('(max-width: 979px)') : null;
    let buildButtonSeen = false;
    let buildButtonAboveViewport = false;
    let observedSelectorRoot = null;
    let selectorResizeObserver = null;

    function bodyOverlayIsOpen() {
      if (!document.body || !document.body.classList
        || typeof document.body.classList.contains !== 'function') return false;
      return ['judes-bundle-overlay-open', 'open-cart', 'open-menu', 'open-search']
        .some((className) => document.body.classList.contains(className));
    }

    function selectorBottomOffset() {
      const selectorRoot = document.querySelector('selector-root.sel-fixed');
      if (selectorResizeObserver && selectorRoot !== observedSelectorRoot) {
        if (observedSelectorRoot) selectorResizeObserver.unobserve(observedSelectorRoot);
        observedSelectorRoot = selectorRoot;
        if (observedSelectorRoot) selectorResizeObserver.observe(observedSelectorRoot);
      }
      if (!selectorRoot || typeof selectorRoot.getBoundingClientRect !== 'function') return 0;
      const styles = typeof root.getComputedStyle === 'function' ? root.getComputedStyle(selectorRoot) : null;
      const ariaHidden = typeof selectorRoot.getAttribute === 'function'
        && selectorRoot.getAttribute('aria-hidden') === 'true';
      if (selectorRoot.hidden || ariaHidden || (styles && (
        styles.display === 'none' || styles.visibility === 'hidden' || styles.opacity === '0'
      ))) return 0;
      const bounds = selectorRoot.getBoundingClientRect();
      const viewportHeight = root.innerHeight || document.documentElement.clientHeight;
      const viewportWidth = root.innerWidth || document.documentElement.clientWidth;
      const hasArea = Number(bounds.width) > 0 && Number(bounds.height) > 0;
      const visible = hasArea
        && bounds.bottom > 0
        && bounds.top < viewportHeight
        && bounds.right > 0
        && bounds.left < viewportWidth;
      return visible ? Math.ceil(Math.max(0, viewportHeight - bounds.top)) : 0;
    }

    function updateStickyEntry() {
      if (!stickyEntry || !stickyBuildButton) return;
      const selectorOffset = `${selectorBottomOffset()}px`;
      if (stickyEntry.style && typeof stickyEntry.style.getPropertyValue === 'function'
        && typeof stickyEntry.style.setProperty === 'function'
        && stickyEntry.style.getPropertyValue('--bundle-sticky-selector-offset') !== selectorOffset) {
        stickyEntry.style.setProperty('--bundle-sticky-selector-offset', selectorOffset);
      }
      const mobile = stickyMobileQuery ? stickyMobileQuery.matches : root.innerWidth < 980;
      const shouldShow = mobile
        && buildButtonSeen
        && buildButtonAboveViewport
        && configPanel.hidden
        && summaryPanel.hidden
        && !overlayLocks.size
        && !activePhotoSwipe
        && !bodyOverlayIsOpen()
        && !document.hidden;
      const desiredHidden = !shouldShow;
      const desiredDisabled = !shouldShow;
      if (stickyEntry.hidden !== desiredHidden) stickyEntry.hidden = desiredHidden;
      if (stickyBuildButton.disabled !== desiredDisabled) stickyBuildButton.disabled = desiredDisabled;
    }

    function stickyMutationIsRelevant(mutation) {
      if (!mutation || mutation.type === 'childList' || mutation.target === document.body) return true;
      const target = mutation.target;
      if (!target) return false;
      if (typeof target.matches === 'function' && target.matches('selector-root')) return true;
      return typeof target.closest === 'function' && !!target.closest('selector-root');
    }

    function handleStickyMutations(mutations) {
      if (!mutations || Array.from(mutations).some(stickyMutationIsRelevant)) updateStickyEntry();
    }

    function setOverlayLock(name, locked) {
      if (locked) overlayLocks.add(name);
      else overlayLocks.delete(name);
      document.body.classList.toggle('judes-bundle-overlay-open', overlayLocks.size > 0);
      updateStickyEntry();
    }

    function reconcileOverlayLocks() {
      overlayLocks.clear();
      if (sheet && !sheet.hidden) overlayLocks.add('information');
      document.body.classList.toggle('judes-bundle-overlay-open', overlayLocks.size > 0);
      updateStickyEntry();
    }

    function selectionsFor(nextRecipe, previousSelections, previousRecipe) {
      return Object.fromEntries(nextRecipe.choiceGroups.map((group) => {
        let entries = previousSelections && previousSelections[group.id]
          ? previousSelections[group.id].filter((variantId) => group.allowedVariantIds.includes(variantId))
            .map((variantId) => ({ variantId, source: group }))
          : [];
        if (!entries.length && group.id.startsWith('covers-') && previousSelections) {
          // Size switch, as on live: a design the new size does not offer at all is dropped and the slots fill
          // from this size's defaults. A design the new size offers keeps its place; when it is unavailable there,
          // resolveSlots moves that slot to the next matching design.
          const [sourceId, previousCoverIds] = Object.entries(previousSelections)
            .find(([groupId]) => groupId.startsWith('covers-')) || ['', []];
          const source = previousRecipe && previousRecipe.choiceGroups.find((candidate) => candidate.id === sourceId);
          entries = previousCoverIds.filter((variantId) => counterpartIn(group, variantId))
            .map((variantId) => ({ variantId, source: source || group }));
        }
        entries = entries.slice(0, group.quantity);
        while (entries.length < group.quantity) entries.push({
          variantId: group.defaultVariantIds[entries.length], source: group, fallbackPresetId: 'most-popular'
        });
        return [group.id, resolveSlots(group, entries)];
      }));
    }

    function matchingRecipe(routineKey, sizeKey) {
      return recipeSource.recipes.find((candidate) => (
        candidate.routineKey === routineKey && candidate.sizeKey === sizeKey
      ));
    }

    function mediaForRecipe() {
      return productMedia[recipe.bundleType] || [];
    }

    function largeImageUrl(value) {
      const url = String(value || '');
      if (/([?&])width=\d+/.test(url)) return url.replace(/([?&])width=\d+/, '$1width=2000');
      return `${url}${url.includes('?') ? '&' : '?'}width=2000`;
    }

    function updatePhotoSwipeDots(container, photoSwipe) {
      const media = mediaForRecipe();
      const dotCount = Math.min(5, media.length);
      if (container.children.length !== dotCount) {
        container.replaceChildren(...Array.from({ length: dotCount }, () => {
          const dot = document.createElement('span');
          dot.className = 'judes-bundle__photoswipe-dot';
          dot.setAttribute('aria-hidden', 'true');
          return dot;
        }));
      }
      const activeDot = media.length < 2 || dotCount < 2
        ? 0
        : Math.round((photoSwipe.currIndex * (dotCount - 1)) / (media.length - 1));
      Array.from(container.children).forEach((dot, index) => {
        dot.classList.toggle('is-active', index === activeDot);
      });
      container.hidden = dotCount < 2;
      container.setAttribute('aria-label', interpolate(copy.gallery.position, {
        current: photoSwipe.currIndex + 1,
        total: media.length
      }));
    }

    async function openMediaModal() {
      const media = mediaForRecipe();
      if (!media.length || activePhotoSwipe) return;
      if (!photoSwipeModuleUrl) throw new Error(copy.gallery.unavailable);
      photoSwipeModulePromise = photoSwipeModulePromise || import(photoSwipeModuleUrl);
      const module = await photoSwipeModulePromise;
      const PhotoSwipe = module.default;
      const photoSwipe = new PhotoSwipe({
        dataSource: media.map((item) => ({
          src: largeImageUrl(item.src),
          width: Number(item.width) || 2000,
          height: Number(item.height) || 2000,
          alt: item.alt || recipe.productHeading
        })),
        index: state.mediaIndex,
        bgOpacity: 1,
        showHideAnimationType: 'none',
        arrowPrev: false,
        arrowNext: false,
        zoom: false,
        counter: false,
        closeOnVerticalDrag: true,
        clickToCloseNonZoomable: false,
        imageClickAction: false,
        tapAction: false,
        doubleTapAction: 'zoom',
        wheelToZoom: true,
        initialZoomLevel: 'fit',
        secondaryZoomLevel: (zoomLevel) => zoomLevel.initial * 3,
        maxZoomLevel: (zoomLevel) => zoomLevel.initial * 3,
        mainClass: 'judes-bundle-photoswipe',
        closeTitle: copy.gallery.close,
        closeSVG: '<svg aria-hidden="true" viewBox="0 0 48 48"><path d="M10 10L38 38M38 10L10 38"/></svg>'
      });

      photoSwipe.on('uiRegister', () => {
        [
          { name: 'judes-prev', label: copy.gallery.previous, image: galleryArrowLeft, action: 'prev' },
          { name: 'judes-next', label: copy.gallery.next, image: galleryArrowRight, action: 'next' }
        ].forEach((control) => {
          photoSwipe.ui.registerElement({
            name: control.name,
            className: `judes-bundle__photoswipe-nav judes-bundle__photoswipe-nav--${control.name === 'judes-prev' ? 'prev' : 'next'}`,
            order: control.name === 'judes-prev' ? 10 : 11,
            isButton: true,
            appendTo: 'wrapper',
            ariaLabel: control.label,
            html: `<img src="${escapeHtml(control.image)}" width="512" height="512" alt="" aria-hidden="true">`,
            onClick: control.action
          });
        });
        photoSwipe.ui.registerElement({
          name: 'judes-dots',
          className: 'judes-bundle__photoswipe-dots',
          order: 12,
          appendTo: 'wrapper',
          onInit: (container, instance) => {
            updatePhotoSwipeDots(container, instance);
            instance.on('change', () => updatePhotoSwipeDots(container, instance));
          }
        });
      });
      photoSwipe.on('change', () => showMedia(photoSwipe.currIndex));
      photoSwipe.on('destroy', () => {
        activePhotoSwipe = null;
        element.__bundlePhotoSwipe = null;
        updateStickyEntry();
        heroImage.focus({ preventScroll: true });
      });
      activePhotoSwipe = photoSwipe;
      element.__bundlePhotoSwipe = photoSwipe;
      updateStickyEntry();
      photoSwipe.init();
    }

    function showMedia(nextIndex) {
      const media = mediaForRecipe();
      if (!media.length || !heroImage) return;
      const normalizedIndex = ((Number(nextIndex) % media.length) + media.length) % media.length;
      const item = media[normalizedIndex];
      state.mediaIndex = normalizedIndex;
      heroImage.src = item.src;
      heroImage.alt = item.alt || recipe.productHeading;
      if (item.width && item.height) {
        heroImage.width = item.width;
        heroImage.height = item.height;
      }
      heroImage.dataset.mediaIndex = String(normalizedIndex);
      heroImage.setAttribute('aria-label', interpolate(copy.gallery.openPosition, {
        current: normalizedIndex + 1,
        total: media.length
      }));

      [normalizedIndex - 1, normalizedIndex + 1].forEach((index) => {
        const adjacent = media[((index % media.length) + media.length) % media.length];
        const preload = new root.Image();
        preload.decoding = 'async';
        preload.src = adjacent.src;
      });
    }

    const information = {
      share: {
        title: copy.information.share50Title,
        body: copy.information.share50Body
      },
      plastic: {
        title: copy.information.plasticTitle,
        body: copy.information.plasticBody
      },
      money: {
        title: copy.information.moneyTitle,
        body: copy.information.moneyBody
      },
      'diaper-free': {
        title: copy.information.diaperFreeTitle,
        body: copy.information.diaperFreeBody,
        hasLink: true
      }
    };

    function format(minorAmount) {
      return formatter.format(Math.ceil(minorAmount / 100));
    }

    function compactFormat(minorAmount) {
      return format(minorAmount);
    }

    function savingsFormat(minorAmount) {
      return formatter.format(Math.floor(minorAmount / 100));
    }

    // Claims (coaching value, disposable savings) are EUR base amounts, not prices: convert them with the shop's
    // presentment rate. EUR visitors keep today's text; without a usable rate they stay in EUR instead of swapping the symbol.
    function eurClaimFormat(minorAmount) {
      const claims = root.JudesMoney;
      if (claims && claims.converting) return claims.format(minorAmount / 100, 'claim', compactFormat(minorAmount));
      if ((element.dataset.currency || 'EUR') === 'EUR') return compactFormat(minorAmount);
      return moneyFormatter('EUR').format(Math.ceil(minorAmount / 100));
    }

    function eurClaimText(value) {
      const claims = root.JudesMoney;
      return claims ? claims.text(value, 'claim') : value;
    }

    let nativeQuote = null;
    let cachedDisplay = null;
    let quoteSerial = 0;
    let submitting = false;
    let awaitingCheckoutNavigation = false;
    let preparedCheckout = null;
    let preparedCheckoutSerial = 0;
    let preparedCheckoutCleanup = Promise.resolve();
    let preparedCheckoutCleanupFailure = null;
    let checkoutAttemptGeneration = 0;
    const priceMessages = {
      de: ['Preis wird bestätigt …', 'Der Setpreis konnte nicht bestätigt werden. Bitte erneut versuchen.', 'Erneut versuchen'],
      en: ['Confirming price …', 'The set price could not be confirmed. Please try again.', 'Try again'],
      fr: ['Confirmation du prix …', 'Le prix du kit n’a pas pu être confirmé. Veuillez réessayer.', 'Réessayer'],
      es: ['Confirmando el precio …', 'No se ha podido confirmar el precio del set. Inténtalo de nuevo.', 'Reintentar'],
      it: ['Conferma del prezzo …', 'Non è stato possibile confermare il prezzo del set. Riprova.', 'Riprova']
    }[(document.documentElement.lang || 'en').slice(0, 2)] || ['Confirming price …', 'The set price could not be confirmed. Please try again.', 'Try again'];
    const priceStatus = query('[data-set-price-status]');
    const priceRetry = query('[data-set-price-retry]');
    priceRetry.textContent = priceMessages[2];
    priceRetry.addEventListener('click', () => refreshNativePrice());

    function selectionKey(result) {
      return JSON.stringify([result.discount.code, commerce.identity(result.cartPayload.items)]);
    }

    function cachedSelection() {
      return {
        country: element.dataset.country,
        currency: element.dataset.currency,
        productId: element.dataset.productId,
        recipeVersion: element.dataset.recipeVersion,
        pricingIdentityVersion: element.dataset.pricingIdentityVersion,
        recipe,
        selections: state.selections,
        catalog
      };
    }

    function activateCachedDisplay(result) {
      // STARTKLAR and explicit variant entry routes stay exact-only even after
      // recipe changes or history.replaceState removes their initial signal.
      const resolved = startPrice && startPriceManifest
        ? startPrice.resolveForRoute(startPriceManifest, cachedSelection(), cacheRouteEligible) : null;
      if (!resolved) {
        cachedDisplay = null;
        element.dataset.cachedPriceValid = 'false';
        return null;
      }
      if (recipe.id === element.dataset.initialRecipeId
        && !startPrice.serverNodeMatches(resolved, serverSeedNode?.dataset)) {
        cachedDisplay = null;
        element.dataset.cachedPriceValid = 'false';
        return null;
      }
      cachedDisplay = { key: selectionKey(result), value: resolved };
      // Liquid never prints cached money. Paint only after the hidden seed and
      // deep manifest have been validated on an eligible initial route.
      price.textContent = compactFormat(resolved.finalPriceMinor);
      element.dataset.cachedPriceValid = 'true';
      return resolved;
    }

    function displayPricing(result) {
      if (result.nativeConfirmed) return result.pricing;
      if (!cachedDisplay || cachedDisplay.key !== selectionKey(result)) return null;
      return {
        ...result.pricing,
        ...cachedDisplay.value,
        savingsMinor: cachedDisplay.value.discountAmountMinor
      };
    }

    function compile() {
      let result;
      try {
        result = core.compileBundle(recipe, catalog, state.selections, { bundleId: state.bundleId });
        if (startklar) result = startklar.apply(result, recipe, catalog, startklarState);
      } catch (error) {
        invalidateNativePrice();
        priceStatus.hidden = false; priceStatus.textContent = priceMessages[1]; priceRetry.hidden = false;
        throw error;
      }
      result.nativeConfirmed = !!nativeQuote && nativeQuote.key === selectionKey(result);
      if (result.nativeConfirmed) {
        result.pricing = { ...result.pricing, ...nativeQuote.value, savingsMinor: nativeQuote.value.discountAmountMinor };
        result.discount.amountMinor = nativeQuote.value.discountAmountMinor;
      }
      return result;
    }

    function disableCheckoutAuthority() {
      element.dataset.nativePriceReady = 'false';
      reviewButton.disabled = true; checkoutButton.disabled = true;
      if (klarnaWrapper) {
        klarnaWrapper.hidden = true;
        klarnaWrapper.querySelector('klarna-placement')?.removeAttribute('data-purchase-amount');
      }
    }

    function hideNativePrice() {
      price.textContent = '…'; retailValue.textContent = ''; giftValue.textContent = '';
      summary.querySelectorAll('.judes-bundle__total strong, .judes-bundle__total s, .judes-bundle__total small').forEach(node => { node.textContent = ''; });
      element.dataset.cachedPriceValid = 'false';
      disableCheckoutAuthority();
      renderOffer(null);
    }

    // Offer render slot (STARTKLAR Preisvorteil). A no-op without the offer link.
    function renderOffer(verifiedValue) {
      if (startklar && typeof startklar.render === 'function') startklar.render(element, entry, recipe, verifiedValue);
    }

    function invalidateNativePrice(preserveCheckoutAttempt = false, retainDisplay = false) {
      if (!preserveCheckoutAttempt) ++checkoutAttemptGeneration;
      void invalidatePreparedCheckout(false);
      ++quoteSerial;
      nativeQuote = null;
      commerce.invalidate(checkoutButton);
      disableCheckoutAuthority();
      if (!retainDisplay) {
        cachedDisplay = null;
        hideNativePrice();
      }
    }

    async function refreshNativePrice(preserveCheckoutAttempt = false) {
      invalidateNativePrice(preserveCheckoutAttempt, true);
      const serial = quoteSerial;
      priceStatus.hidden = false; priceStatus.textContent = priceMessages[0]; priceRetry.hidden = true;
      try {
        const selected = compile();
        const seed = activateCachedDisplay(selected);
        if (seed) {
          renderPrice(selected);
          renderSummary(selected);
        } else hideNativePrice();
        const key = selectionKey(selected);
        const value = await commerce.quote(selected.cartPayload.items, selected.discount.code, (verified) => {
          if (serial !== quoteSerial || key !== selectionKey(compile())) return;
          // Paint only; nativeQuote, payment widgets and buttons await cleanup.
          paintVerifiedPrice(selected, verified);
        });
        if (serial !== quoteSerial || key !== selectionKey(compile())) return false;
        // Offer hook: a personal code Shopify allocated nothing to is dropped once.
        if (startklar && typeof startklar.rejectCredit === 'function' && startklar.rejectCredit(entry, value, recipe)) {
          const refreshed = await refreshNativePrice(preserveCheckoutAttempt);
          return preserveCheckoutAttempt ? false : refreshed;
        }
        nativeQuote = { key, value, at: Date.now() };
        stockRounds = 0;
        renderPrice(compile()); renderSummary(compile());
        reviewButton.disabled = false; checkoutButton.disabled = submitting;
        priceStatus.hidden = true;
        if (klarnaWrapper) klarnaWrapper.hidden = false;
        element.dataset.nativePriceReady = 'true';
        return true;
      } catch (error) {
        // Stock hook: this market's exact quote had too few units of a chosen design. Learn the limit, move the
        // affected slots to the next matching design and quote the new selection (render quotes again).
        if (serial === quoteSerial && !error.quoteCleanupFailed && applyStockShortfalls(error)) {
          render();
          return false;
        }
        // Offer hook: Shopify rejected the personal code itself: drop it once and quote the normal set.
        if (serial === quoteSerial && !error.quoteCleanupFailed && startklar && typeof startklar.rejectCredit === 'function'
          && startklar.rejectCredit(entry, error, recipe)) {
          const refreshed = await refreshNativePrice(preserveCheckoutAttempt);
          return preserveCheckoutAttempt ? false : refreshed;
        }
        if (serial === quoteSerial) {
          let current = null;
          try { current = compile(); } catch (compileError) { current = null; }
          if (current && cachedDisplay && cachedDisplay.key === selectionKey(current)) {
            renderPrice(current); renderSummary(current); disableCheckoutAuthority();
          } else hideNativePrice();
          priceStatus.hidden = false; priceStatus.textContent = priceMessages[1]; priceRetry.hidden = false;
        }
        return false;
      }
    }

    // Bounded: every round only lowers limits, and at most four automatic substitutions follow each other.
    let stockRounds = 0;
    function applyStockShortfalls(error) {
      const shortfalls = error && Array.isArray(error.stockShortfalls) ? error.stockShortfalls : [];
      if (!shortfalls.length || stockRounds >= 4) return false;
      shortfalls.forEach((shortfall) => {
        const variantId = String(shortfall.variantId);
        const units = Number.isSafeInteger(shortfall.available) && shortfall.available >= 0
          ? shortfall.available
          : Math.max(0, Number(shortfall.requested || 1) - 1);
        marketLimits.set(variantId, Math.min(limitOf(variantId), units));
      });
      const activePreset = ['most-popular', 'soft-pastels'].includes(state.presetId)
        ? recipe.presets.find((preset) => preset.id === state.presetId) : null;
      const resolved = activePreset ? resolvePreset(recipe, activePreset) : resolveSelections(recipe, state.selections);
      if (sameSelections(resolved, state.selections)) return false;
      stockRounds += 1;
      state.selections = resolved;
      if (!activePreset) state.presetId = '';
      return true;
    }

    function isPreparedCheckoutCurrent(serial, key) {
      if (serial !== preparedCheckoutSerial || summaryPanel.hidden) return false;
      try {
        return key === selectionKey(compile());
      } catch (error) {
        return false;
      }
    }

    function invalidatePreparedCheckout(cancelCheckoutAttempt = true) {
      if (cancelCheckoutAttempt) ++checkoutAttemptGeneration;
      const record = preparedCheckout;
      if (!record) return preparedCheckoutCleanup;
      preparedCheckout = null;
      ++preparedCheckoutSerial;
      const cleanup = preparedCheckoutCleanup.then(async () => {
        let staged;
        try {
          staged = await record.promise;
        } catch (error) {
          if (unsafePreparedCheckoutFailure(error)) throw error;
          return null;
        }
        const restored = await checkout.unstage({ routesRoot, staged, fetchFn: root.fetch.bind(root) });
        commerce.invalidate(checkoutButton, true);
        return restored;
      });
      preparedCheckoutCleanup = cleanup.catch((error) => {
        preparedCheckoutCleanupFailure = error;
        showError(new Error(`${copy.errors.discountFailed} ${copy.errors.reviewCart}`));
        throw error;
      });
      void preparedCheckoutCleanup.catch(() => {});
      return preparedCheckoutCleanup;
    }

    function primeCheckoutPreparation() {
      if (preparedCheckoutCleanupFailure) {
        showError(new Error(`${copy.errors.discountFailed} ${copy.errors.reviewCart}`));
        return Promise.reject(preparedCheckoutCleanupFailure);
      }
      if (!nativeQuote || element.dataset.nativePriceReady !== 'true') return Promise.resolve(null);
      const result = compile();
      if (!result.nativeConfirmed) return Promise.resolve(null);
      const key = selectionKey(result);
      if (preparedCheckout && preparedCheckout.key === key && !preparedCheckout.error) return preparedCheckout.promise;
      if (preparedCheckout && preparedCheckout.key === key) preparedCheckout = null;
      const serial = ++preparedCheckoutSerial;
      const cleanupBarrier = preparedCheckoutCleanup;
      const record = { key, serial, staged: null, error: null, promise: null };
      record.promise = (async () => {
        await cleanupBarrier;
        if (preparedCheckoutCleanupFailure) throw preparedCheckoutCleanupFailure;
        const verification = await commerce.prepare(result.cartPayload, result.discount.code, nativeQuote.value, checkoutButton);
        if (!isPreparedCheckoutCurrent(serial, key)) throw new Error('The selection changed, please review the new total');
        try {
          const staged = await checkout.stage({
            routesRoot,
            discountCode: result.discount.code,
            cartPayload: result.cartPayload,
            verification,
            isCurrent: () => isPreparedCheckoutCurrent(serial, key),
            fetchFn: root.fetch.bind(root)
          });
          record.staged = staged;
          return staged;
        } finally {
          commerce.invalidate(checkoutButton, true);
        }
      })();
      record.promise.catch((error) => { record.error = error; });
      preparedCheckout = record;
      return record.promise;
    }

    function clearError() {
      errorBox.hidden = true;
      errorBox.textContent = '';
    }

    function showError(error) {
      errorBox.textContent = error.message || String(error);
      errorBox.hidden = false;
      errorBox.focus();
    }

    function openInformation(key, trigger) {
      const content = information[key];
      if (!content || !sheet) return;
      sheetTrigger = trigger || null;
      if (key === 'share' && recipe.metrics.share === '25 %' && copy.information.share25Title) {
        sheetTitle.textContent = copy.information.share25Title;
        sheetBody.textContent = copy.information.share25Body;
      } else if (key === 'share') {
        sheetTitle.textContent = recipe.metrics.share === '50 %'
          ? copy.information.share50Title
          : copy.information.share100Title;
        sheetBody.textContent = recipe.metrics.share === '50 %'
          ? copy.information.share50Body
          : copy.information.share100Body;
      } else {
        sheetTitle.textContent = content.title;
        sheetBody.textContent = key === 'money' ? eurClaimText(content.body) : content.body;
      }
      if (sheetLink) sheetLink.hidden = !content.hasLink;
      sheet.hidden = false;
      setOverlayLock('information', true);
      sheetClose.focus();
    }

    function closeInformation() {
      if (!sheet || sheet.hidden) return;
      sheet.hidden = true;
      if (sheetLink) sheetLink.hidden = true;
      setOverlayLock('information', false);
      if (sheetTrigger) sheetTrigger.focus();
      sheetTrigger = null;
    }

    function variantImage(variant, size) {
      if (!variant || !variant.image) return '<span class="judes-bundle__image-placeholder" aria-hidden="true"></span>';
      const dimension = size || 96;
      return `<img src="${escapeHtml(sizedImageUrl(variant.image, dimension))}" alt="" width="${dimension}" height="${dimension}" loading="lazy" decoding="async">`;
    }

    function itemLabel(variantId) {
      const configured = (recipe.fixedItems || []).find((item) => String(item.variantId) === String(variantId));
      if (configured && configured.label) return label(configured.label);
      const variant = catalog[variantId];
      return displayTitle(variant.title) || variant.productTitle;
    }

    function renderGiftStrip(result) {
      const usesGiftPricing = result.pricing.mode === 'gift_discount' || !!result.startklar;
      giftSection.hidden = !usesGiftPricing;
      if (!usesGiftPricing) {
        giftStrip.replaceChildren();
        giftHeading.textContent = '';
        return;
      }
      const gifts = result.giftComponents.map((component) => {
        const variant = catalog[component.variantId];
        return `<article class="judes-bundle__gift-card">
          ${variantImage(variant, 84)}
          <strong>${component.quantity} × ${escapeHtml(itemLabel(component.variantId))}</strong>
          <s>${compactFormat(variant.priceMinor * component.quantity)}</s>
          <b>${copy.free}</b>
        </article>`;
      });

      const lockedIds = [
        recipe.choiceGroups.find((group) => group.id === 'small-wetbags')?.allowedVariantIds?.[0],
        recipe.fixedItems.find((item) => item.giftQuantity === 0)?.variantId
      ].filter(Boolean);

      (result.startklar ? [] : lockedIds).forEach((variantId) => {
        const variant = catalog[variantId];
        if (!variant) return;
        gifts.push(`<article class="judes-bundle__gift-card is-locked">
          <span class="judes-bundle__gift-lock">⌑</span>
          ${variantImage(variant, 84)}
          <strong>${escapeHtml(variant.productTitle)}</strong>
          <s>${compactFormat(variant.priceMinor)}</s>
          <b>${copy.locked}</b>
        </article>`);
      });

      giftStrip.innerHTML = gifts.join('');
      giftHeading.textContent = `${copy.worth} ${compactFormat(result.pricing.giftValueMinor)}`;
    }

    function renderIncluded(result) {
      const totalComponents = result.paidComponents.concat(result.giftComponents);
      const componentQuantities = new Map();
      totalComponents.forEach((component) => {
        componentQuantities.set(
          component.variantId,
          (componentQuantities.get(component.variantId) || 0) + component.quantity
        );
      });

      function includedImage(variant, imageUrl) {
        if (imageUrl) {
          return `<img src="${escapeHtml(sizedImageUrl(imageUrl, 76))}" alt="" width="76" height="76" loading="lazy" decoding="async">`;
        }
        return variantImage(variant, 76);
      }

      function includedRow({ variant, label, quantity, status, imageUrl }) {
        const value = status
          ? `<span class="judes-bundle__included-status">${escapeHtml(status)}</span>`
          : `<span class="judes-bundle__included-qty">× ${quantity}</span>`;
        return `<li class="judes-bundle__included-row">
          ${includedImage(variant, imageUrl)}
          <span class="judes-bundle__included-name">${escapeHtml(label)}</span>
          ${value}
        </li>`;
      }

      const customisableRows = recipe.choiceGroups.map((group) => {
        const selectedVariantId = state.selections[group.id][0];
        return includedRow({
          variant: catalog[selectedVariantId],
          label: label(group.includedLabel || group.label),
          quantity: group.quantity
        });
      }).join('');

      const essentialRows = [...recipe.fixedItems].sort((left, right) => (
        (left.includedOrder || 0) - (right.includedOrder || 0)
      )).map((item) => {
        const totalQuantity = componentQuantities.get(item.variantId) || 0;
        const bonusQuantity = result.startklar
          ? result.giftComponents.filter((component) => component.variantId === item.variantId).reduce((sum, component) => sum + component.quantity, 0)
          : 0;
        const quantity = (totalQuantity - bonusQuantity) * (item.includedQuantityMultiplier || 1);
        if (!quantity) return '';
        return includedRow({
          variant: catalog[item.variantId],
          label: label(item.includedLabel || item.label || itemLabel(item.variantId)),
          quantity
        });
      }).join('');

      const giftNames = result.giftComponents.map((component) => {
        const prefix = component.quantity > 1 ? `${component.quantity} × ` : '';
        return `${prefix}${itemLabel(component.variantId)}`;
      });
      const firstGift = result.giftComponents[0];
      const coachingVariant = catalog[state.selections[recipe.choiceGroups[0].id][0]];
      const usesGiftPricing = result.pricing.mode === 'gift_discount' || !!result.startklar;
      const bonusRows = [
        includedRow({
          variant: coachingVariant,
          imageUrl: bundleCoachingImage,
          label: `${label('Judes family coaching')} (${copy.coachingValue} ${eurClaimFormat(20000)})`,
          status: usesGiftPricing ? copy.free.toUpperCase() : copy.included
        }),
        giftNames.length ? includedRow({
          variant: catalog[firstGift.variantId],
          label: giftNames.join(', '),
          status: copy.free.toUpperCase()
        }) : ''
      ].join('');

      included.innerHTML = `
        <section class="judes-bundle__included-group">
          <h4>${copy.customisableItems}</h4>
          <ul>${customisableRows}</ul>
        </section>
        <section class="judes-bundle__included-group">
          <h4>${copy.essentialItems}</h4>
          <ul>${essentialRows}</ul>
        </section>
        <section class="judes-bundle__included-group">
          <h4>${copy.bonusItems}</h4>
          <ul>${bonusRows}</ul>
        </section>`;
    }

    function renderPreset(preset) {
      const resolved = resolvePreset(recipe, preset);
      const previewIds = recipe.choiceGroups.flatMap((group) => resolved[group.id] || []).slice(0, 4);
      return `<button type="button" class="judes-bundle__preset" data-bundle-preset="${escapeHtml(preset.id)}" aria-pressed="${preset.id === state.presetId}">
        <span>${escapeHtml(copy.preset[preset.label] || preset.label)}</span>
        <i class="judes-bundle__collection-preview">${previewIds.map((variantId) => variantImage(catalog[variantId], 48)).join('')}</i>
      </button>`;
    }

    // Returns the labelled design collections to show, or null for the flat grid. Groups only list allowed,
    // available designs; a group with a limit (Most Popular) shows its first `limit` of them; empty groups
    // disappear. If any selectable design is missing from every shown group, the flat grid keeps it reachable.
    function visibleDesignGroups(group) {
      if (!Array.isArray(group.designGroups) || !group.designGroups.length) return null;
      const selectable = (variantId) => designSelectable(group, variantId);
      const visible = group.designGroups.map((designGroup) => {
        const variantIds = (designGroup.variantIds || []).filter(selectable);
        return {
          id: String(designGroup.id),
          label: String(designGroup.label),
          variantIds: designGroup.limit ? variantIds.slice(0, designGroup.limit) : variantIds
        };
      }).filter((designGroup) => designGroup.variantIds.length);
      const shown = new Set(visible.flatMap((designGroup) => designGroup.variantIds));
      if (!group.allowedVariantIds.filter(selectable).every((variantId) => shown.has(variantId))) return null;
      return visible;
    }

    function renderGroup(group) {
      const selected = state.selections[group.id];
      const slots = selected.map((variantId, slotIndex) => {
        const variant = catalog[variantId];
        return `<button type="button" class="judes-bundle__slot" data-bundle-slot="${group.id}:${slotIndex}" aria-label="${escapeHtml(copy.change)} ${escapeHtml(label(group.label))} ${slotIndex + 1}" aria-controls="bundle-picker-${escapeHtml(group.id)}" aria-expanded="false">
          ${variantImage(variant, 112)}<i aria-hidden="true">✎</i>
        </button>`;
      }).join('');

      const option = (variantId) => {
        const variant = catalog[variantId];
        const isSelected = selected[0] === variantId;
        return `<button type="button" class="judes-bundle__option" data-bundle-option="${group.id}:${variantId}" aria-pressed="${isSelected}">
          ${variantImage(variant, 140)}
          <span>${escapeHtml(displayTitle(variant.title) || variant.productTitle)}</span>
        </button>`;
      };

      const designGroups = visibleDesignGroups(group);
      const options = designGroups
        ? designGroups.map((designGroup) => {
          const titleId = `bundle-design-group-${group.id}-${designGroup.id}`;
          return `<div class="judes-bundle__design-group" role="group" aria-labelledby="${escapeHtml(titleId)}" data-bundle-design-group="${escapeHtml(designGroup.id)}">
          <h4 class="judes-bundle__design-group-title" id="${escapeHtml(titleId)}">${escapeHtml(designGroup.label)}</h4>
          <div class="judes-bundle__design-group-options">${designGroup.variantIds.map(option).join('')}</div>
        </div>`;
        }).join('')
        : group.allowedVariantIds.filter((variantId) => designAvailable(variantId)).map(option).join('');
      const pickerClass = designGroups ? 'judes-bundle__picker judes-bundle__picker--grouped' : 'judes-bundle__picker';

      return `<section class="judes-bundle__group" data-bundle-group="${group.id}">
        <div class="judes-bundle__group-head">
          <h3>${escapeHtml(label(group.label))}</h3>
          <button type="button" data-bundle-edit="${group.id}" aria-controls="bundle-picker-${escapeHtml(group.id)}" aria-expanded="false">${copy.editDesigns} ✎ <span>· × ${group.quantity}</span></button>
        </div>
        <div class="judes-bundle__slots">${slots}</div>
        <div id="bundle-picker-${escapeHtml(group.id)}" class="${pickerClass}" data-bundle-picker="${group.id}" hidden>${options}</div>
      </section>`;
    }

    function summaryRows(components, role) {
      const quantities = new Map();
      components.forEach((component) => {
        quantities.set(component.variantId, (quantities.get(component.variantId) || 0) + component.quantity);
      });
      return Array.from(quantities.entries()).map(([variantId, quantity]) => {
        const variant = catalog[variantId];
        const badge = role === 'gift' ? `<strong>${escapeHtml(copy.free)}</strong>` : '';
        return `<li>${variantImage(variant, 64)}<span>${quantity} × ${escapeHtml(itemLabel(variantId))}</span>${badge}</li>`;
      }).join('');
    }

    function renderSummary(result) {
      const displayed = displayPricing(result);
      const selectedSections = recipe.choiceGroups.map((group) => {
        const components = state.selections[group.id].map((variantId) => ({ variantId, quantity: 1 }));
        return `<h4>${escapeHtml(label(group.label))}</h4><ul>${summaryRows(components, 'paid')}</ul>`;
      }).join('');
      const essentials = result.paidComponents.filter((component) => !component.groupId);
      const giftSummary = result.pricing.mode === 'gift_discount' || result.startklar
        ? `<h4>${copy.freeGifts}</h4><ul>${summaryRows(result.giftComponents, 'gift')}</ul>`
        : '';
      const savingsText = !displayed ? '' : result.pricing.mode === 'gift_discount'
        ? `${result.giftComponents.length} ${copy.freeGifts.toLowerCase()} · ${copy.worth} ${compactFormat(result.pricing.giftValueMinor)}`
        : `${copy.youSave} ${savingsFormat(displayed.savingsMinor)}`;

      summary.innerHTML = `<section class="judes-bundle__summary-card">
        <h3>${copy.yourSetIncludes}</h3>
        ${selectedSections}
        <h4>${copy.essentials}</h4>
        <ul>${summaryRows(essentials, 'paid')}</ul>
        ${giftSummary}
      </section>
      <div class="judes-bundle__total">
        <span>${copy.total}</span>
        <strong>${displayed ? compactFormat(displayed.finalPriceMinor) : ''}</strong>
        <s>${displayed ? compactFormat(displayed.retailValueMinor) : ''}</s>
        <small>${savingsText}</small>
      </div>`;
      renderOffer(result.nativeConfirmed ? result.pricing : null);
    }

    function paintVerifiedPrice(selected, value) {
      price.textContent = compactFormat(value.finalPriceMinor);
      retailValue.textContent = compactFormat(value.retailValueMinor);
      const savings = selected.pricing.mode === 'gift_discount'
        ? `${selected.giftComponents.length} ${copy.freeGifts.toLowerCase()} · ${copy.worth} ${compactFormat(selected.pricing.giftValueMinor)}`
        : `${copy.youSave} ${savingsFormat(value.discountAmountMinor)}`;
      giftValue.textContent = savings;
      for (const [selector, text] of [
        ['strong', price.textContent], ['s', retailValue.textContent], ['small', savings]
      ]) {
        const node = summary.querySelector(`.judes-bundle__total ${selector}`);
        if (node) node.textContent = text;
      }
      priceStatus.hidden = true;
      renderOffer(value);
    }

    function renderPrice(result) {
      const displayed = displayPricing(result);
      if (!displayed) { hideNativePrice(); return; }
      price.textContent = compactFormat(displayed.finalPriceMinor);
      retailValue.textContent = compactFormat(displayed.retailValueMinor);
      giftValue.textContent = result.pricing.mode === 'gift_discount'
        ? `${result.giftComponents.length} ${copy.freeGifts.toLowerCase()} · ${copy.worth} ${compactFormat(result.pricing.giftValueMinor)}`
        : `${copy.youSave} ${savingsFormat(displayed.savingsMinor)}`;
      element.dataset.bundlePricingMode = result.pricing.mode;
      if (result.nativeConfirmed) syncKlarnaPrice(displayed.finalPriceMinor);
      renderOffer(result.nativeConfirmed ? result.pricing : null);
    }

    function withoutVariantParameter(value) {
      const url = new URL(value, root.location.href);
      url.searchParams.delete('variant');
      return `${url.pathname}${url.search}${url.hash}`;
    }

    function syncKlarnaPrice(finalPriceMinor) {
      // A recipe without a parent set variant (Starter) removes the variant
      // parameter instead of writing a variant that does not exist.
      const selectedVariantId = recipe.parentVariantId ? String(recipe.parentVariantId) : '';
      const url = new URL(root.location.href);
      if (selectedVariantId) url.searchParams.set('variant', selectedVariantId);
      else url.searchParams.delete('variant');
      let nextUrl = startklar ? startklar.syncUrl(url.href, recipe, startklarState) : `${url.pathname}${url.search}${url.hash}`;
      if (!selectedVariantId) nextUrl = withoutVariantParameter(nextUrl);
      root.history.replaceState(root.history.state, '', nextUrl);

      const placement = klarnaWrapper && klarnaWrapper.querySelector('klarna-placement');
      if (!placement) return;

      const configs = Array.isArray(root.appBlockPlacements) ? root.appBlockPlacements : [];
      const config = configs.find((candidate) => candidate.selector === placement.id);
      const variants = config && Array.isArray(config.productVariants) ? config.productVariants : [];
      // The Klarna app block resolves the amount from the URL variant, otherwise
      // from its first available variant. A parentless recipe (Starter) has no
      // variant parameter, so its price is written into that fallback entry; the
      // next parent recipe rewrites its own entry before Klarna is shown again.
      const parentVariant = selectedVariantId
        ? variants.find((variant) => String(variant.id) === selectedVariantId)
        : variants.find((variant) => variant && variant.available) || variants[0];
      if (parentVariant) parentVariant.price = finalPriceMinor;
      if (config) config.variantPrice = String(finalPriceMinor);
      placement.setAttribute('data-purchase-amount', String(finalPriceMinor));
    }

    function renderImpactMeters() {
      const segmentCounts = [
        recipe.metrics.shareSegments,
        recipe.metrics.plasticSegments,
        recipe.metrics.savingsSegments
      ];
      element.querySelectorAll('.judes-bundle__impact-grid i').forEach((meter, meterIndex) => {
        const activeCount = segmentCounts[meterIndex] || 0;
        [...meter.children].forEach((segment, segmentIndex) => {
          segment.dataset.active = String(segmentIndex < activeCount);
        });
      });
    }

    function render() {
      let result;
      try { result = compile(); } catch (error) { return; }
      activateCachedDisplay(result);
      productHeading.textContent = copy.productHeading[recipe.bundleType] || recipe.productHeading;
      renderPrice(result);
      routine.textContent = recipe.routineLabel;
      summaryRoutine.textContent = recipe.routineLabel;
      summarySize.textContent = copy.sizeLabel[recipe.sizeKey] || recipe.sizeLabel;
      description.textContent = copy.descriptions[recipe.routineKey] || recipe.description;
      perks.innerHTML = translatedPerks(recipe).map((perk) => `<li>${escapeHtml(perk)}</li>`).join('');
      impactShare.textContent = recipe.metrics.share;
      impactShareLabel.textContent = copy.impactSize[recipe.sizeKey];
      impactPlastic.textContent = recipe.metrics.plastic;
      impactSavings.textContent = eurClaimText(recipe.metrics.savings);
      renderImpactMeters();
      summaryBenefits.innerHTML = translatedPerks(recipe).concat([
        interpolate(copy.impactWasteTemplate, { value: recipe.metrics.plastic }),
        interpolate(copy.impactSavingsTemplate, { value: eurClaimText(recipe.metrics.savings) })
      ]).map((benefit) => `<li>${escapeHtml(benefit)}</li>`).join('');
      const premium = recipe.bundleType === 'premium';
      if (state.mediaType !== recipe.bundleType) {
        state.mediaType = recipe.bundleType;
        state.mediaIndex = 0;
      }
      showMedia(state.mediaIndex);
      const cardImage = premium ? element.dataset.bundlePremiumCardImage : element.dataset.bundleClassicCardImage;
      const summaryImageUrl = premium ? element.dataset.bundlePremiumSummaryImage : element.dataset.bundleClassicSummaryImage;
      const routineImage = query('.judes-bundle__routine-image');
      const summaryImage = query('.judes-bundle__summary-hero');
      if (routineImage && cardImage) routineImage.src = cardImage;
      if (summaryImage && summaryImageUrl) summaryImage.src = summaryImageUrl;
      element.querySelectorAll('[data-bundle-routine-option]').forEach((button) => {
        button.setAttribute('aria-pressed', String(button.dataset.bundleRoutineOption === recipe.routineKey));
      });
      element.querySelectorAll('[data-bundle-size-option]').forEach((button) => {
        button.setAttribute('aria-pressed', String(button.dataset.bundleSizeOption === recipe.sizeKey));
      });
      element.dataset.startklarCode = result.startklar ? result.startklar.code : '';
      renderGiftStrip(result);
      renderIncluded(result);
      presets.innerHTML = (recipe.presets || []).map(renderPreset).join('');
      groups.innerHTML = recipe.choiceGroups.map(renderGroup).join('');
      renderSummary(result);
      bindDynamicControls();
      refreshNativePrice();
    }

    function changeRecipe(routineKey, sizeKey) {
      if (submitting) return;
      const nextRecipe = matchingRecipe(routineKey, sizeKey);
      if (!nextRecipe || nextRecipe.id === recipe.id) return;
      invalidateNativePrice(false, true);
      const previousSelections = state.selections;
      const previousRecipe = recipe;
      recipe = nextRecipe;
      const activePreset = ['most-popular', 'soft-pastels'].includes(state.presetId)
        ? recipe.presets.find((preset) => preset.id === state.presetId) : null;
      state.selections = activePreset ? resolvePreset(recipe, activePreset) : selectionsFor(recipe, previousSelections, previousRecipe);
      state.bundleId = newBundleId(recipe.id);
      state.presetId = activePreset ? activePreset.id : '';
      clearError();
      render();
    }

    function syncPickerSelection(groupId, slotIndex) {
      const selectedVariantId = state.selections[groupId][slotIndex];
      element.querySelectorAll(`[data-bundle-option^="${groupId}:"]`).forEach((option) => {
        const optionVariantId = option.dataset.bundleOption.split(':')[1];
        option.setAttribute('aria-pressed', String(optionVariantId === selectedVariantId));
      });
    }

    function updateSelectedSlot(groupId, slotIndex, variantId) {
      if (submitting) return;
      const activeSlot = query(`[data-bundle-slot="${groupId}:${slotIndex}"]`);
      const picker = query(`[data-bundle-picker="${groupId}"]`);
      const groupElement = query(`[data-bundle-group="${groupId}"]`);
      if (state.selections[groupId][slotIndex] === variantId) {
        picker.hidden = true;
        activeSlot.setAttribute('aria-pressed', 'true');
        activeSlot.setAttribute('aria-expanded', 'false');
        query(`[data-bundle-edit="${groupId}"]`).setAttribute('aria-expanded', 'false');
        activeSlot.focus({ preventScroll: true });
        root.requestAnimationFrame(() => groupElement.scrollIntoView({ behavior: scrollBehavior(), block: 'start' }));
        return;
      }
      invalidateNativePrice(false, true);
      state.selections[groupId][slotIndex] = variantId;
      state.presetId = '';
      clearError();
      const resolved = resolveSelections(recipe, state.selections);
      if (!sameSelections(resolved, state.selections)) {
        // Only after a quote taught this market's limit: too many units of one design move to the next match.
        state.selections = resolved;
        render();
        return;
      }

      let result;
      try { result = compile(); } catch (error) { return; }
      activateCachedDisplay(result);
      const group = recipe.choiceGroups.find((candidate) => candidate.id === groupId);
      const affectsGift = group.giftQuantity > 0 && slotIndex >= group.quantity - group.giftQuantity;
      const currentVisual = activeSlot.querySelector('img, .judes-bundle__image-placeholder');
      const replacement = document.createElement('template');
      replacement.innerHTML = variantImage(catalog[variantId], 112);
      currentVisual.replaceWith(replacement.content.firstElementChild);

      element.querySelectorAll('[data-bundle-preset]').forEach((button) => {
        button.setAttribute('aria-pressed', 'false');
      });
      syncPickerSelection(groupId, slotIndex);
      renderPrice(result);
      if (affectsGift) renderGiftStrip(result);
      if (slotIndex === 0 || affectsGift) renderIncluded(result);
      renderSummary(result);
      refreshNativePrice();
      activeSlot.setAttribute('aria-pressed', 'true');
      activeSlot.setAttribute('aria-expanded', 'false');
      query(`[data-bundle-edit="${groupId}"]`).setAttribute('aria-expanded', 'false');
      picker.hidden = true;
      activeSlot.focus({ preventScroll: true });
      root.requestAnimationFrame(() => groupElement.scrollIntoView({ behavior: scrollBehavior(), block: 'start' }));
    }

    function openPicker(groupId, slotIndex) {
      const picker = query(`[data-bundle-picker="${groupId}"]`);
      const opening = picker.hidden;
      element.querySelectorAll('[data-bundle-picker]').forEach((candidate) => { candidate.hidden = true; });
      element.querySelectorAll('[data-bundle-slot], [data-bundle-edit]').forEach((candidate) => {
        candidate.removeAttribute('aria-pressed');
        candidate.setAttribute('aria-expanded', 'false');
      });
      picker.dataset.activeSlot = String(slotIndex || 0);
      picker.hidden = !opening;
      if (opening) {
        syncPickerSelection(groupId, Number(slotIndex || 0));
        element.querySelectorAll(`[data-bundle-slot^="${groupId}:"], [data-bundle-edit="${groupId}"]`).forEach((trigger) => {
          trigger.setAttribute('aria-expanded', 'true');
        });
        picker.scrollIntoView({ behavior: scrollBehavior(), block: 'nearest' });
      }
    }

    function bindDynamicControls() {
      element.querySelectorAll('[data-bundle-preset]').forEach((button) => {
        button.addEventListener('click', () => {
          if (submitting) return;
          const preset = recipe.presets.find((candidate) => candidate.id === button.dataset.bundlePreset);
          invalidateNativePrice(false, true);
          state.selections = resolvePreset(recipe, preset);
          state.presetId = preset.id;
          clearError();
          render();
        });
      });

      element.querySelectorAll('[data-bundle-slot]').forEach((button) => {
        button.addEventListener('click', () => {
          const [groupId, slotIndex] = button.dataset.bundleSlot.split(':');
          openPicker(groupId, Number(slotIndex));
          button.setAttribute('aria-pressed', 'true');
        });
      });

      element.querySelectorAll('[data-bundle-edit]').forEach((button) => {
        button.addEventListener('click', () => openPicker(button.dataset.bundleEdit, 0));
      });

      element.querySelectorAll('[data-bundle-option]').forEach((button) => {
        button.addEventListener('click', () => {
          const [groupId, variantId] = button.dataset.bundleOption.split(':');
          const picker = query(`[data-bundle-picker="${groupId}"]`);
          const slotIndex = Number(picker.dataset.activeSlot || 0);
          updateSelectedSlot(groupId, slotIndex, variantId);
        });
      });
    }

    buildButton.addEventListener('click', () => {
      clearError();
      configPanel.hidden = false;
      updateStickyEntry();
      configPanel.scrollIntoView({ behavior: scrollBehavior(), block: 'start' });
    });

    if (stickyEntry && stickyBuildButton) {
      stickyBuildButton.addEventListener('click', () => {
        if (stickyBuildButton.disabled) return;
        stickyEntry.hidden = true;
        stickyBuildButton.disabled = true;
        buildButton.click();
      });

      if (typeof root.IntersectionObserver === 'function') {
        const buildButtonObserver = new root.IntersectionObserver((entries) => {
          const entry = entries[0];
          if (!entry) return;
          if (entry.isIntersecting) {
            buildButtonSeen = true;
            buildButtonAboveViewport = false;
          } else {
            buildButtonAboveViewport = buildButtonSeen && entry.boundingClientRect.bottom <= 0;
          }
          updateStickyEntry();
        });
        buildButtonObserver.observe(buildButton);
      } else if (typeof buildButton.getBoundingClientRect === 'function') {
        const checkBuildButtonPosition = () => {
          const bounds = buildButton.getBoundingClientRect();
          const viewportHeight = root.innerHeight || document.documentElement.clientHeight;
          const visible = bounds.bottom > 0 && bounds.top < viewportHeight;
          if (visible) buildButtonSeen = true;
          buildButtonAboveViewport = buildButtonSeen && bounds.bottom <= 0;
          updateStickyEntry();
        };
        root.addEventListener('scroll', checkBuildButtonPosition, { passive: true });
        root.addEventListener('resize', checkBuildButtonPosition);
        checkBuildButtonPosition();
      }

      const handleStickyViewportChange = () => updateStickyEntry();
      if (stickyMobileQuery && typeof stickyMobileQuery.addEventListener === 'function') {
        stickyMobileQuery.addEventListener('change', handleStickyViewportChange);
      } else if (stickyMobileQuery && typeof stickyMobileQuery.addListener === 'function') {
        stickyMobileQuery.addListener(handleStickyViewportChange);
      }
      if (typeof root.MutationObserver === 'function' && document.body) {
        const bodyClassObserver = new root.MutationObserver(handleStickyMutations);
        bodyClassObserver.observe(document.body, {
          attributes: true,
          attributeFilter: ['aria-hidden', 'class', 'hidden', 'style'],
          childList: true,
          subtree: true
        });
      }
      if (typeof root.ResizeObserver === 'function') {
        selectorResizeObserver = new root.ResizeObserver(updateStickyEntry);
      }
      root.addEventListener('resize', handleStickyViewportChange);
      root.addEventListener('orientationchange', handleStickyViewportChange);
      updateStickyEntry();
    }

    element.querySelectorAll('[data-bundle-routine-option]').forEach((button) => {
      button.addEventListener('click', () => changeRecipe(button.dataset.bundleRoutineOption, recipe.sizeKey));
    });

    element.querySelectorAll('[data-bundle-size-option]').forEach((button) => {
      button.addEventListener('click', () => changeRecipe(recipe.routineKey, button.dataset.bundleSizeOption));
    });

    reviewButton.addEventListener('click', () => {
      try {
        clearError();
        if (!compile().nativeConfirmed) return;
        renderSummary(compile());
        summaryPanel.hidden = false;
        updateStickyEntry();
        summaryPanel.scrollIntoView({ behavior: scrollBehavior(), block: 'start' });
        void primeCheckoutPreparation().catch(() => {});
      } catch (error) {
        showError(error);
      }
    });

    backButton.addEventListener('click', () => {
      summaryPanel.hidden = true;
      updateStickyEntry();
      void invalidatePreparedCheckout().catch(() => {});
      configPanel.scrollIntoView({ behavior: scrollBehavior(), block: 'start' });
    });

    function restoreCheckoutInteraction() {
      awaitingCheckoutNavigation = false;
      submitting = false;
      setCheckoutProgress(checkoutButton, false, checkoutButtonText, checkoutLanguage,
        typeof checkoutBusyText === 'undefined' ? undefined : checkoutBusyText);
      checkoutButton.disabled = !nativeQuote || element.dataset.nativePriceReady !== 'true';
    }

    function unsafePreparedCheckoutFailure(error) {
      return !!(error && (error.partialCart || error.unsafeCleanup || error.cleanupUnsafe
        || error.cleanupFailed || error.quoteCleanupFailed));
    }

    function beginCheckoutAttempt() {
      return ++checkoutAttemptGeneration;
    }

    function checkoutAttemptIsCurrent(attempt) {
      return attempt === checkoutAttemptGeneration && !summaryPanel.hidden;
    }

    async function continueCheckoutAttempt(attempt) {
      if (checkoutAttemptIsCurrent(attempt)) return true;
      try {
        await preparedCheckoutCleanup;
      } catch (error) {
        throw preparedCheckoutCleanupFailure || error;
      }
      return false;
    }

    async function checkoutFromPreparedStage(attempt) {
      if (!checkoutAttemptIsCurrent(attempt)) return false;
      if (preparedCheckoutCleanupFailure) throw preparedCheckoutCleanupFailure;
      if (!nativeQuote || Date.now() - nativeQuote.at > 30000) {
        await invalidatePreparedCheckout(false);
        if (!await continueCheckoutAttempt(attempt)) return false;
        const refreshed = await refreshNativePrice(true);
        if (!await continueCheckoutAttempt(attempt)) return false;
        if (!refreshed) return false;
      }
      let result = compile();
      if (!result.nativeConfirmed) return false;
      let key = selectionKey(result);
      const record = preparedCheckout && preparedCheckout.key === key ? preparedCheckout : null;
      let staged = null;
      if (record) {
        try {
          staged = await record.promise;
        } catch (error) {
          if (unsafePreparedCheckoutFailure(error) || preparedCheckoutCleanupFailure) {
            throw preparedCheckoutCleanupFailure || error;
          }
          if (!await continueCheckoutAttempt(attempt)) return false;
          if (preparedCheckout === record) {
            preparedCheckout = null;
            ++preparedCheckoutSerial;
          }
        }
      }
      if (!await continueCheckoutAttempt(attempt)) return false;

      if (staged && preparedCheckout === record && isPreparedCheckoutCurrent(record.serial, key)
        && Date.now() - staged.stagedAt <= 30000 && checkoutAttemptIsCurrent(attempt)) {
        root.location.assign(`${routesRoot}checkout`);
        return true;
      }

      if (record && preparedCheckout === record) {
        await invalidatePreparedCheckout(false);
        if (!await continueCheckoutAttempt(attempt)) return false;
      }
      result = compile();
      key = selectionKey(result);
      if (!result.nativeConfirmed || !checkoutAttemptIsCurrent(attempt)) return false;
      const verification = await commerce.prepare(result.cartPayload, result.discount.code, nativeQuote.value, checkoutButton);
      if (!await continueCheckoutAttempt(attempt)) return false;
      if (!verification || key !== selectionKey(compile()) || !checkoutAttemptIsCurrent(attempt)) return false;
      try {
        await checkout.submit({
          routesRoot,
          discountCode: result.discount.code,
          cartPayload: result.cartPayload,
          verification,
          isCurrent: () => checkoutAttemptIsCurrent(attempt) && key === selectionKey(compile()),
          fetchFn: root.fetch.bind(root),
          navigate: (url) => root.location.assign(url)
        });
      } catch (error) {
        if (error.checkoutCanceled && !unsafePreparedCheckoutFailure(error)
          && !checkoutAttemptIsCurrent(attempt)) {
          await continueCheckoutAttempt(attempt);
          return false;
        }
        throw error;
      }
      if (!await continueCheckoutAttempt(attempt)) return false;
      commerce.invalidate(checkoutButton, true);
      return true;
    }

    async function handleCheckoutClick() {
      if (submitting) return;
      submitting = true;
      checkoutButton.disabled = true;
      let checkoutSucceeded = false;
      setCheckoutProgress(checkoutButton, true, checkoutButtonText, checkoutLanguage,
        typeof checkoutBusyText === 'undefined' ? undefined : checkoutBusyText);
      clearError();
      try {
        if (typeof checkoutFromPreparedStage === 'function') {
          const checkoutAttempt = typeof beginCheckoutAttempt === 'function' ? beginCheckoutAttempt() : null;
          checkoutSucceeded = await checkoutFromPreparedStage(checkoutAttempt);
          if (checkoutSucceeded) awaitingCheckoutNavigation = true;
          return;
        }
        if (!nativeQuote || Date.now() - nativeQuote.at > 30000) {
          if (!await refreshNativePrice()) return;
        }
        const result = compile();
        if (!result.nativeConfirmed) return;
        const key = selectionKey(result);
        const verification = await commerce.prepare(result.cartPayload, result.discount.code, nativeQuote.value, checkoutButton);
        if (!verification || key !== selectionKey(compile())) return;
        await checkout.submit({
          routesRoot,
          discountCode: result.discount.code,
          cartPayload: result.cartPayload,
          verification,
          isCurrent: () => key === selectionKey(compile()),
          fetchFn: root.fetch.bind(root),
          navigate: (url) => root.location.assign(url)
        });
        commerce.invalidate(checkoutButton, true);
        awaitingCheckoutNavigation = true;
        checkoutSucceeded = true;
      } catch (error) {
        commerce.invalidate(checkoutButton, true);
        showError(new Error(`${copy.errors.discountFailed} ${copy.errors.reviewCart}`));
      } finally {
        if (!checkoutSucceeded) restoreCheckoutInteraction();
      }
    }

    checkoutButton.addEventListener('click', handleCheckoutClick);

    function releaseCartScroll() {
      root.setTimeout(() => document.body.classList.remove('open-cart'), 0);
    }

    document.querySelectorAll('section.cart .cartc').forEach((button) => {
      button.addEventListener('click', releaseCartScroll);
      button.addEventListener('keydown', (event) => {
        if (event.key === 'Escape' || event.key === 'Enter' || event.key === ' ') releaseCartScroll();
      });
    });

    heroPrevious.addEventListener('click', () => showMedia(state.mediaIndex - 1));
    heroNext.addEventListener('click', () => showMedia(state.mediaIndex + 1));
    heroImage.addEventListener('click', () => {
      if (suppressHeroClick) {
        suppressHeroClick = false;
        return;
      }
      openMediaModal().catch(showError);
    });
    heroImage.addEventListener('keydown', (event) => {
      if (event.key !== 'Enter' && event.key !== ' ') return;
      event.preventDefault();
      openMediaModal().catch(showError);
    });
    let touchStart = null;
    heroMedia.addEventListener('touchstart', (event) => {
      const touch = event.changedTouches[0];
      touchStart = { x: touch.clientX, y: touch.clientY };
    }, { passive: true });
    heroMedia.addEventListener('touchend', (event) => {
      if (!touchStart) return;
      const touch = event.changedTouches[0];
      const deltaX = touch.clientX - touchStart.x;
      const deltaY = touch.clientY - touchStart.y;
      touchStart = null;
      if (Math.abs(deltaX) < 45 || Math.abs(deltaX) <= Math.abs(deltaY)) return;
      suppressHeroClick = true;
      root.setTimeout(() => { suppressHeroClick = false; }, 500);
      showMedia(state.mediaIndex + (deltaX < 0 ? 1 : -1));
    }, { passive: true });

    element.querySelectorAll('[data-bundle-info]').forEach((button) => {
      button.addEventListener('click', () => openInformation(button.dataset.bundleInfo, button));
    });

    sheetClose.addEventListener('click', closeInformation);
    sheet.addEventListener('click', (event) => {
      if (event.target === sheet) closeInformation();
    });
    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') {
        closeInformation();
      }
    });
    function handlePageShow() {
      reconcileOverlayLocks();
      if (awaitingCheckoutNavigation) restoreCheckoutInteraction();
    }

    root.addEventListener('pageshow', handlePageShow);
    document.addEventListener('visibilitychange', () => {
      if (!document.hidden) reconcileOverlayLocks();
      else updateStickyEntry();
    });
    element.addEventListener('touchstart', reconcileOverlayLocks, { passive: true });

    if (startPriceManifest) {
      const delay = Math.min(2147483647, Math.max(0, startPriceManifest.expiresAt - Date.now()));
      const expiryTimer = root.setTimeout(() => {
        startPriceManifest = null;
        cachedDisplay = null;
        element.dataset.cachedPriceValid = 'false';
        try {
          const result = compile();
          if (!result.nativeConfirmed) {
            hideNativePrice();
            renderSummary(result);
          }
        } catch (error) {
          hideNativePrice();
        }
      }, delay);
      if (expiryTimer && typeof expiryTimer.unref === 'function') expiryTimer.unref();
    }

    render();
    element.dataset.bundleViewerInteraction = 'photoswipe';
    element.dataset.bundleReady = 'true';
  }

  whenReady(() => {
    document.querySelectorAll('[data-judes-bundle-builder]').forEach((element) => {
      try {
        createBuilder(element);
      } catch (error) {
        const errorBox = element.querySelector('[data-bundle-error]');
        errorBox.textContent = error.message || String(error);
        errorBox.hidden = false;
      }
    });
  });
}(typeof globalThis !== 'undefined' ? globalThis : this));
